import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Slider,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Group as CohortsIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  LocationOn as LocationIcon,
  School as EducationIcon,
  Work as WorkIcon,
  Language as LanguageIcon,
  HealthAndSafety as HealthIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import FilterToggleButton from '../../components/map/FilterToggleButton';
import './ThreeDMap.css';

const PopulationCohortsTray: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  // All state and filter logic as in PopulationCohorts.tsx
  const [showFilters, setShowFilters] = useState(true);
  const [timeRange, setTimeRange] = useState('1year');
  const [selectedPayors, setSelectedPayors] = useState<string[]>([]);
  const [selectedHealthPlans, setSelectedHealthPlans] = useState<string[]>([]);
  const [incomeRange, setIncomeRange] = useState<number[]>([0, 200000]);
  const [educationLevel, setEducationLevel] = useState<string[]>([]);
  const [employmentStatus, setEmploymentStatus] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [healthLiteracy, setHealthLiteracy] = useState<string[]>([]);
  const [selectedSex, setSelectedSex] = useState<string[]>([]);
  const [selectedRace, setSelectedRace] = useState<string[]>([]);
  const [selectedContractTypes, setSelectedContractTypes] = useState<string[]>([]);
  const [selectedHealthMetrics, setSelectedHealthMetrics] = useState<string[]>([]);

  // Demographics Accordion
  const renderDemographicsFilters = () => (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { m: 0 } }}>
        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500, fontSize: '0.875rem' }}>
          <CohortsIcon fontSize="small" color="primary" />
          Demographics
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Age Range
            </Typography>
            <Slider value={[0, 100]} onChange={() => {}} valueLabelDisplay="auto" min={0} max={100} />
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Sex
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={selectedSex} onChange={(e) => setSelectedSex(e.target.value as string[])}>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Race/Ethnicity
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={selectedRace} onChange={(e) => setSelectedRace(e.target.value as string[])}>
                <MenuItem value="white">White</MenuItem>
                <MenuItem value="black">Black/African American</MenuItem>
                <MenuItem value="hispanic">Hispanic/Latino</MenuItem>
                <MenuItem value="asian">Asian</MenuItem>
                <MenuItem value="native">Native American</MenuItem>
                <MenuItem value="pacific">Pacific Islander</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Household Size
            </Typography>
            <Slider value={[1, 10]} onChange={() => {}} valueLabelDisplay="auto" min={1} max={10} marks />
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  // Payor Mix Accordion
  const renderPayorMixFilters = () => (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { m: 0 } }}>
        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500, fontSize: '0.875rem' }}>
          <PaymentIcon fontSize="small" color="primary" />
          Payor Mix
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Insurance Type
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={selectedPayors} onChange={(e) => setSelectedPayors(e.target.value as string[])}>
                <MenuItem value="commercial">Commercial</MenuItem>
                <MenuItem value="medicare">Medicare</MenuItem>
                <MenuItem value="medicaid">Medicaid</MenuItem>
                <MenuItem value="uninsured">Uninsured</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Contract Type
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={selectedContractTypes} onChange={(e) => setSelectedContractTypes(e.target.value as string[])}>
                <MenuItem value="ffs">Fee-for-Service</MenuItem>
                <MenuItem value="risk">Risk Contracts</MenuItem>
                <MenuItem value="value">Value-Based</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Health Plans
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={selectedHealthPlans} onChange={(e) => setSelectedHealthPlans(e.target.value as string[])}>
                <MenuItem value="plan1">Plan A</MenuItem>
                <MenuItem value="plan2">Plan B</MenuItem>
                <MenuItem value="plan3">Plan C</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  // SDOH Accordion
  const renderSDOHFilters = () => (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { m: 0 } }}>
        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500, fontSize: '0.875rem' }}>
          <HealthIcon fontSize="small" color="primary" />
          Social Determinants of Health
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Income Level
            </Typography>
            <Slider value={incomeRange} onChange={(_, value) => setIncomeRange(value as number[])} valueLabelDisplay="auto" min={0} max={200000} valueLabelFormat={(value) => `$${value.toLocaleString()}`}/>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Education Level
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={educationLevel} onChange={(e) => setEducationLevel(e.target.value as string[])}>
                <MenuItem value="less_than_hs">Less than High School</MenuItem>
                <MenuItem value="hs">High School</MenuItem>
                <MenuItem value="some_college">Some College</MenuItem>
                <MenuItem value="associates">Associate's Degree</MenuItem>
                <MenuItem value="bachelors">Bachelor's Degree</MenuItem>
                <MenuItem value="masters">Master's Degree</MenuItem>
                <MenuItem value="doctorate">Doctorate</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Employment Status
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={employmentStatus} onChange={(e) => setEmploymentStatus(e.target.value as string[])}>
                <MenuItem value="employed">Employed</MenuItem>
                <MenuItem value="unemployed">Unemployed</MenuItem>
                <MenuItem value="retired">Retired</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
                <MenuItem value="student">Student</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Language Spoken
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={languages} onChange={(e) => setLanguages(e.target.value as string[])}>
                <MenuItem value="english">English</MenuItem>
                <MenuItem value="spanish">Spanish</MenuItem>
                <MenuItem value="mandarin">Mandarin</MenuItem>
                <MenuItem value="vietnamese">Vietnamese</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Health Literacy Level
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={healthLiteracy} onChange={(e) => setHealthLiteracy(e.target.value as string[])}>
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="proficient">Proficient</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  // Population Trends Accordion
  const renderPopulationTrendsFilters = () => (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { m: 0 } }}>
        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500, fontSize: '0.875rem' }}>
          <TrendingUpIcon fontSize="small" color="primary" />
          Population Trends
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Time Period
            </Typography>
            <FormControl fullWidth size="small">
              <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                <MenuItem value="1month">Last Month</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="1year">Last Year</MenuItem>
                <MenuItem value="5years">Last 5 Years</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Migration Patterns
            </Typography>
            <FormControlLabel control={<Switch size="small" />} label="Show Migration Overlay" />
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Health Metrics
            </Typography>
            <FormControl fullWidth size="small">
              <Select multiple value={selectedHealthMetrics} onChange={(e) => setSelectedHealthMetrics(e.target.value as string[])}>
                <MenuItem value="conditions">Common Conditions</MenuItem>
                <MenuItem value="status">Health Status</MenuItem>
                <MenuItem value="ed">ED Visits</MenuItem>
                <MenuItem value="births">Live Births</MenuItem>
                <MenuItem value="deaths">Deaths</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  // Cohort Actions Accordion
  const renderCohortActions = () => (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { m: 0 } }}>
        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500, fontSize: '0.875rem' }}>
          <CohortsIcon fontSize="small" color="primary" />
          Cohort Actions
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Load Existing Cohort
            </Typography>
            <FormControl fullWidth size="small">
              <Select>
                <MenuItem value="cohort1">High-Risk Patients</MenuItem>
                <MenuItem value="cohort2">Chronic Care</MenuItem>
                <MenuItem value="cohort3">Preventive Care</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Find Cohorts
            </Typography>
            <FormControl fullWidth size="small">
              <Select>
                <MenuItem value="revenue">Highest Revenue</MenuItem>
                <MenuItem value="volume">Highest Volume</MenuItem>
                <MenuItem value="lives">Largest Patient Base</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} fullWidth>
            Create New Cohort
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box className="population-cohorts-tray adaptive-scenario-tray">
      <Box className="population-cohorts-tray-header">
        <Typography variant="h6">Population & Cohorts</Typography>
        <IconButton size="small" onClick={onClose} className="population-cohorts-close-btn">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className="population-cohorts-tray-controls">
        <FilterToggleButton showFilters={showFilters} onToggle={() => setShowFilters(!showFilters)} />
        <Button variant="contained" size="small" sx={{ ml: 1 }} startIcon={<AddIcon />}>New Cohort</Button>
      </Box>
      <Box className="population-cohorts-tray-content">
        {showFilters && (
          <Box className="population-cohorts-filters">
            {renderDemographicsFilters()}
            {renderPayorMixFilters()}
            {renderSDOHFilters()}
            {renderPopulationTrendsFilters()}
            {renderCohortActions()}
          </Box>
        )}
        {/* You can add a map or other content here if needed */}
      </Box>
    </Box>
  );
};

export default PopulationCohortsTray;
